import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";
import { type TDialCodes } from "@src/api/useDialCodes";
import Container from "@src/components/shared/Container";
import PhoneSelect from "@src/components/shared/PhoneSelect";
import TitleAndDesc from "@src/components/shared/TitleAndDesc";
import { Button } from "@src/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@src/components/ui/form";
import { Input } from "@src/components/ui/input";
import useZustandStore from "@src/helpers/zustand";
import { cn } from "@src/lib/utils";
import { useSendOtp } from "@src/api/useSendOtp";
import { useKYCAddUserLog, LoggerTypes } from "@src/api/useKYCAddUserLog";

export const NUMBERS_ONLY_REGEX = /^[0-9]*$/;

const FormSchema = z.object({
  dialCode: z.string().refine((val) => val),
  phone: z.string().refine((v) => v),
});

function Contact() {
  const addUserLogMutation = useKYCAddUserLog();
  const { t } = useTranslation();
  const { data: globalState, setData } = useZustandStore((state) => state);
  const sendOtpMutation = useSendOtp();

  const [selectedCountry, setSelectedCountry] = useState({
    countryName: "საქართველო",
    countryCode: "GE",
    dialCode: "995",
    minDIG: 9,
    maxDIG: 9,
    flagURL: "https://cdn.payunicard.ge/Files/Flags/GE.svg",
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      dialCode: globalState.dialCode || "995",
      phone: globalState.phone,
    },
  });

  useEffect(() => {
    addUserLogMutation.mutate({
      eventName: LoggerTypes.Contact,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSubmit(data: z.infer<typeof FormSchema>) {
    setData({
      ...globalState,
      dialCode: data.dialCode,
      phone: data.phone,
    });
    sendOtpMutation.mutate({
      apiKey: globalState.apiKey,
      phone: `${data.dialCode}${data.phone}`,
    });
  }

  const phone = form.watch("phone");

  return (
    <Container className="max-md:min-h-[90vh] max-md:bg-white">
      <Form {...form}>
        <div className="py-4">
          <TitleAndDesc desc={t("contact.desc")} title={t("contact.title")} />
        </div>
        <form
          className="flex flex-col gap-3 max-md:mb-40"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-5">
            <div className="flex gap-2">
              <FormField
                control={form.control}
                name="dialCode"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <FormControl>
                        <PhoneSelect
                          dialCode={field.value}
                          setCountry={(country: TDialCodes) => {
                            form.setValue("dialCode", country.dialCode);
                            form.setValue("phone", "");
                            form.getFieldState("phone").isTouched = false;
                            // @ts-ignore
                            setSelectedCountry(country);
                          }}
                        />
                      </FormControl>
                    </FormItem>
                  );
                }}
              />
              <Input
                className={cn(
                  form.getFieldState("phone").isTouched &&
                    (phone.length < selectedCountry.minDIG ||
                      phone.length > selectedCountry.maxDIG) &&
                    "border-red-500"
                )}
                max={selectedCountry.maxDIG}
                min={selectedCountry.minDIG}
                {...form.register("phone")}
                label={t("contact.phone")}
                onChange={({ target }) => {
                  if (
                    (NUMBERS_ONLY_REGEX.test(target.value) || !target.value) &&
                    target.value.length <= selectedCountry.maxDIG
                  ) {
                    form.setValue("phone", target.value);
                  }
                }}
                value={phone}
              />
            </div>
          </div>
          <div className="z-10 max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0">
            <div className="rounded-6 w-full self-end bg-white py-8 max-md:px-4 ">
              <Button
                className="w-full"
                disabled={
                  phone.length < selectedCountry.minDIG ||
                  phone.length > selectedCountry.maxDIG ||
                  sendOtpMutation.isLoading
                }
                type="submit"
              >
                {t("continue")}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Container>
  );
}

export default Contact;
